<template>
  <Layout class="layout--basic">
    <HeaderRoot title="Podcasts" />
    <div class="layout__wrapper">
      <EarlyAccessBannerPodcast />
      <v-wait for="Error Loading Podcasts">
        <template v-slot:waiting>
          <LoadingError />
        </template>
      </v-wait>
      <v-wait class="layout__wrapper-inner container" for="Loading Podcasts">
        <template v-slot:waiting>
          <loading-spinner />
        </template>

        <LatestEpisodesBlock
          v-if="followedEpisodesView"
          :view="followedEpisodesView"
        />
        <section class="docs-listing docs-listing--podcasts">
          <ListDocs v-if="podcastsView" :view="podcastsView" />
        </section>
      </v-wait>
    </div>
  </Layout>
</template>

<script setup lang="ts">
import { onBeforeMount, Raw, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import Layout from '@/app/components/Layout.vue'
import HeaderRoot from '@/app/components/header/Root.vue'
import LatestEpisodesBlock from '@/app/components/listing/LatestEpisodesBlock.vue'
import ListDocs from '@/app/components/listing/ListDocs.vue'
import EarlyAccessBannerPodcast from '@/app/components/doc/EarlyAccessBannerPodcast.vue'
import LoadingError from '@/components/ui/LoadingError.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'

import { backend } from '@/services/backend'
import { Sentry } from '@/services/sentry'

import { PodcastsView } from '@/models/docs.list.podcasts'
import { createFollowedPodcastEpisodesView, ListView } from '@/models/docs.list'

const wait = useWait()

const podcastsView = ref<Raw<ListView> | null>(null)
const followedEpisodesView = ref<Raw<ListView> | null>(null)

onBeforeMount(async () => {
  wait.start('Loading Podcasts')

  try {
    await Promise.all([loadPodcasts(), loadFollowedEpisodes()])
    wait.end('Loading Podcasts')
  } catch (error) {
    wait.start('Error Loading Podcasts')
    Sentry.captureException(error)
  }
})

async function loadPodcasts(): Promise<void> {
  const response = await backend.getPodcasts()
  const podcasts = new PodcastsView(response)
  podcastsView.value = new ListView('All Podcasts', podcasts)
}

async function loadFollowedEpisodes(): Promise<void> {
  // This function has a try/catch block so that it doesn't
  // prevent the page from loading if there's an error
  try {
    followedEpisodesView.value = await createFollowedPodcastEpisodesView()
  } catch (error: any) {
    Sentry.captureException(error)
  }
}
</script>

<style lang="scss" scoped>
:deep(.docs-listing__list) {
  @media only screen and (min-width: $w-medium-b) and (max-width: $w-tablet-s) {
    grid-template-columns: 1fr 1fr;
  }
}

:deep(.filter) {
  @media only screen and (max-width: $w-tablet-s) {
    display: block;
  }
}
</style>
